/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

// Only the content players: the v3 audio player and the Plyr stream video. Anything else on the page
// that plays sound (browser extensions, notification sounds) is left alone.
const CONTENT_MEDIA_SELECTOR = '.v3-audio-player audio, .plyr video';

/**
 * Keeps one audio/video playing at a time: when a content player starts, every other content player on
 * the page is paused. Media events don't bubble, so a single capture-phase listener on the document sees
 * every player, including ones spread across separate `ptl-resource-preview` instances.
 */
@Injectable({
  providedIn: 'root',
})
export class SingleMediaPlaybackService {
  private listening = false;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  start(): void {
    if (this.listening) {
      return;
    }
    this.listening = true;
    this.document.addEventListener('play', (event) => this.pauseOthers(event.target), true);
  }

  private pauseOthers(startedMedia: EventTarget | null): void {
    if (!(startedMedia instanceof HTMLMediaElement) || !startedMedia.matches(CONTENT_MEDIA_SELECTOR)) {
      return;
    }

    const contentMedia = this.document.querySelectorAll<HTMLMediaElement>(CONTENT_MEDIA_SELECTOR);
    for (let i = 0; i < contentMedia.length; i++) {
      const media = contentMedia[i];
      if (media !== startedMedia && !media.paused) {
        media.pause();
      }
    }
  }
}
